import React from 'react'
import {Spinner} from 'react-bootstrap'
import {ReportColumn} from '../types'
import {format} from 'date-fns'

interface ReportTableProps {
  columns: ReportColumn[]
  data: Array<Record<string, any>>
  isLoading: boolean
  sortBy?: string
  sortOrder: 'asc' | 'desc'
  onSortChange: (field: string) => void
  page: number
  pageSize: number
  totalCount: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

const ReportTable: React.FC<ReportTableProps> = ({
  columns,
  data,
  isLoading,
  sortBy,
  sortOrder,
  onSortChange,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
}) => {
  const formatValue = (value: any, column: ReportColumn) => {
    if (value === null || value === undefined) return '-'

    const columnType = column.type?.toLowerCase()
    switch (columnType) {
      case 'currency':
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          minimumFractionDigits: 2,
        }).format(Number(value))

      case 'number':
        return new Intl.NumberFormat('en-IN').format(Number(value))

      case 'percentage':
        return `${Number(value).toFixed(2)}%`

      case 'date':
        try {
          return format(new Date(value), 'dd-MMM-yyyy')
        } catch {
          return String(value)
        }

      case 'datetime':
        try {
          return format(new Date(value), 'dd-MMM-yyyy hh:mm a')
        } catch {
          return String(value)
        }

      case 'boolean':
        return value ? 'Yes' : 'No'

      default:
        return String(value)
    }
  }

  const visibleColumns = columns.filter((col) => col.visible)
  const totalPages = Math.ceil(totalCount / pageSize)

  const getAlignClass = (type: string) => {
    const lowerType = type?.toLowerCase()
    return ['number', 'currency', 'percentage'].includes(lowerType) ? 'text-end' : 'text-start'
  }

  const isDangerColumn = (field: string) => {
    const lowerField = field.toLowerCase()
    return (
      lowerField.includes('outstanding') ||
      lowerField.includes('due') ||
      lowerField.includes('overdue') ||
      lowerField.includes('payable')
    )
  }

  const isSuccessColumn = (field: string) => {
    const lowerField = field.toLowerCase()
    return (
      lowerField.includes('collected') ||
      lowerField.includes('received') ||
      lowerField.includes('totalsales')
    )
  }

  const isInfoColumn = (field: string) => {
    const lowerField = field.toLowerCase()
    return (
      (lowerField.includes('paid') || lowerField.includes('adjusted')) &&
      !lowerField.includes('payable')
    )
  }

  const getHeaderStyle = (field: string): React.CSSProperties => {
    if (isDangerColumn(field)) return {color: '#f1416c'}
    if (isSuccessColumn(field)) return {color: '#50cd89'}
    if (isInfoColumn(field)) return {color: '#009ef7'}
    return {}
  }

  const getCellStyle = (column: ReportColumn, value: any): React.CSSProperties => {
    const numValue = Number(value)

    if (column.colorRule && column.colorRule !== 'NONE') {
      switch (column.colorRule) {
        case 'POSITIVE_GREEN_NEGATIVE_RED':
        case 'NEGATIVE_RED_POSITIVE_GREEN':
          if (numValue < 0) return {color: '#f1416c', fontWeight: 600}
          if (numValue > 0) return {color: '#50cd89', fontWeight: 600}
          return {}

        case 'DISCOUNT_RED_GAIN_GREEN':
          if (value === 'Discount') return {color: '#f1416c', fontWeight: 600}
          if (value === 'Gain') return {color: '#50cd89', fontWeight: 600}
          return {}

        case 'POSITIVE_RED':
          if (value && numValue > 0) return {color: '#f1416c', fontWeight: 600}
          return {}

        case 'POSITIVE_GREEN':
          if (value && numValue > 0) return {color: '#50cd89', fontWeight: 600}
          return {}

        case 'POSITIVE_BLUE':
          if (value && numValue > 0) return {color: '#009ef7', fontWeight: 600}
          return {}
      }
    }

    if (isDangerColumn(column.field) && value && numValue > 0) {
      return {color: '#f1416c', fontWeight: 600}
    }
    if (isSuccessColumn(column.field) && value && numValue > 0) {
      return {color: '#50cd89', fontWeight: 600}
    }
    if (isInfoColumn(column.field) && value && numValue > 0) {
      return {color: '#009ef7', fontWeight: 600}
    }
    return {}
  }

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center py-10'>
        <Spinner animation='border' variant='primary' />
        <span className='ms-3'>Loading data...</span>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className='text-center py-10 text-muted'>
        <i className='bi bi-inbox fs-1 d-block mb-3'></i>
        <span>No data available for the selected filters</span>
      </div>
    )
  }

  return (
    <>
      <div className='table-responsive'>
        <table className='table table-row-bordered table-hover align-middle gs-0 gy-4'>
          <thead>
            <tr className='fw-bold text-muted text-uppercase fs-7 bg-light'>
              {visibleColumns.map((col) => (
                <th
                  key={col.field}
                  className={`${getAlignClass(col.type)} ${
                    col.sortable ? 'cursor-pointer user-select-none' : ''
                  } py-4 px-3`}
                  style={{
                    ...(col.width ? {width: `${col.width}%`} : {}),
                    ...getHeaderStyle(col.field),
                  }}
                  onClick={() => col.sortable && onSortChange(col.field)}
                >
                  {col.displayName}
                  {col.sortable && (
                    <span className='ms-1'>
                      {sortBy === col.field ? (
                        sortOrder === 'asc' ? (
                          <i className='bi bi-arrow-up'></i>
                        ) : (
                          <i className='bi bi-arrow-down'></i>
                        )
                      ) : (
                        <i className='bi bi-arrow-down-up text-gray-400'></i>
                      )}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='text-gray-700'>
            {data.map((row, rowIndex) => (
              <tr
                key={row._id || row.id || rowIndex}
              >
                {visibleColumns.map((col) => (
                  <td
                    key={col.field}
                    className={`${getAlignClass(col.type)} py-3 px-3`}
                    style={getCellStyle(col, row[col.field])}
                  >
                    {formatValue(row[col.field], col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='d-flex justify-content-between align-items-center mt-5 pt-3 border-top d-print-none'>
        <div className='text-muted fs-7'>
          Showing{' '}
          <span className='fw-semibold text-gray-700'>{(page - 1) * pageSize + 1}</span> to{' '}
          <span className='fw-semibold text-gray-700'>
            {Math.min(page * pageSize, totalCount)}
          </span>{' '}
          of <span className='fw-semibold text-gray-700'>{totalCount}</span> entries
        </div>
        <div className='d-flex align-items-center gap-4'>
          <select
            className='form-select form-select-sm form-select-solid'
            style={{width: '80px'}}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <nav>
            <ul className='pagination pagination-sm mb-0'>
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button
                  className='page-link rounded-start'
                  onClick={() => onPageChange(page - 1)}
                  disabled={page === 1}
                >
                  <i className='bi bi-chevron-left'></i>
                </button>
              </li>
              {Array.from({length: Math.min(5, totalPages)}, (_, i) => {
                let pageNum: number
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (page <= 3) {
                  pageNum = i + 1
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = page - 2 + i
                }
                if (pageNum < 1 || pageNum > totalPages) return null
                return (
                  <li key={pageNum} className={`page-item ${pageNum === page ? 'active' : ''}`}>
                    <button className='page-link' onClick={() => onPageChange(pageNum)}>
                      {pageNum}
                    </button>
                  </li>
                )
              })}
              <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                <button
                  className='page-link rounded-end'
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages}
                >
                  <i className='bi bi-chevron-right'></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </>
  )
}

export default ReportTable
